import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  ShortPayRecommendationResponseSchema,
  DismissibleArCollectionActionSchema,
} from "./ar-short-pay-recommendations.contract";

/**
 * AR Over-Payment Recommendation RPC Contract
 *
 * Twin of the short-pay recommendation contract: an AI agent reviews the
 * evidence assembled on an `over_payment` collection thread and produces a
 * ranked list of recommended actions (refund, apply credit, issue credit memo,
 * loop in the rep) drawn from the shared ArCollectionAction taxonomy, each
 * grounded in the specific evidence behind it.
 *
 * The request/response shapes are identical to short-pay's, so the schemas are
 * reused directly rather than re-declared. Auto-generated when an over-payment
 * thread is created; regenerable on demand. Results are cached per-thread in the
 * generic content-addressed intelligence cache with a data fingerprint for
 * staleness.
 */
export const arOverPaymentRecommendationsContracts = {
  /** Retrieve the cached recommendation for a thread (no AI call). */
  get: oc
    .input(z.object({ threadId: z.string().uuid() }))
    .output(ShortPayRecommendationResponseSchema)
    .route({ method: "GET" }),
  /** Trigger a fresh AI analysis for a thread and cache it. */
  regenerate: oc
    .input(z.object({ threadId: z.string().uuid() }))
    .output(ShortPayRecommendationResponseSchema)
    .route({ method: "POST" }),
  /**
   * Mute a recommended action for this thread's customer ("don't suggest this
   * again"). Writes a customer-scoped memory bank note and returns the cached
   * recommendation with the action already filtered out. Org-wide: every thread
   * for that customer stops surfacing the action.
   */
  dismissAction: oc
    .input(
      z.object({
        threadId: z.string().uuid(),
        action: DismissibleArCollectionActionSchema,
        /** Optional free-text reason, stored on the note for later review. */
        reason: z.string().trim().max(500).optional(),
      }),
    )
    .output(ShortPayRecommendationResponseSchema)
    .route({ method: "POST" }),
} as const;
